import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { userService, authService } from '../../services/firebaseService';

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // Vérifier si l'utilisateur est connecté
        const currentUser = authService.getCurrentUser();
        if (!currentUser) {
          navigate('/login');
          return;
        }

        // Récupérer le profil utilisateur depuis Firebase
        const userProfileData = await userService.getUserProfile();
        
        if (userProfileData && userProfileData.user) {
          setUser(userProfileData.user);
        } else {
          setError('Profil utilisateur non trouvé');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement du profil:', err);
        setError('Erreur lors du chargement du profil: ' + (err.message || 'Erreur inconnue'));
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch (err) {
      console.error('Erreur lors de la déconnexion:', err);
      setError('Erreur lors de la déconnexion: ' + (err.message || 'Erreur inconnue'));
    }
  };

  const handleCancelSubscription = async () => {
    try {
      // Vérifier si l'utilisateur est connecté
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        navigate('/login');
        return;
      }

      // Confirmer l'annulation
      if (!window.confirm('Êtes-vous sûr de vouloir annuler votre abonnement ? Vous pourrez continuer à utiliser votre abonnement jusqu\'à la fin de la période en cours.')) {
        return;
      }

      // Mettre à jour le profil utilisateur avec l'abonnement annulé
      await userService.updateProfile({
        subscriptionCancelled: true,
        // La date de fin d'abonnement reste la même, l'abonnement sera simplement non renouvelé
      });

      alert('Votre abonnement sera annulé à la fin de la période en cours.');
      
      // Rafraîchir les données utilisateur
      const userProfileData = await userService.getUserProfile();
      if (userProfileData && userProfileData.user) {
        setUser(userProfileData.user);
      }
    } catch (err) {
      console.error('Erreur lors de l\'annulation de l\'abonnement:', err);
      setError('Erreur lors de l\'annulation de l\'abonnement: ' + (err.message || 'Erreur inconnue'));
    }
  };

  if (loading) {
    return <div className="text-center mt-5"><div className="spinner-border" role="status"></div></div>;
  }

  if (!user) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          Vous devez être connecté pour accéder à cette page.
          <Link to="/login" className="btn btn-primary ms-3">Se connecter</Link>
        </div>
      </div>
    );
  }

  // Formater les dates
  const formatDate = (dateValue) => {
    if (!dateValue) return 'Non définie';
    
    let date;
    // Vérifier si la date est un objet Firestore Timestamp
    if (dateValue && typeof dateValue === 'object' && dateValue.toDate && typeof dateValue.toDate === 'function') {
      // Convertir le Timestamp Firestore en Date
      date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      // Déjà un objet Date
      date = dateValue;
    } else {
      // Essayer de parser la chaîne de caractères
      date = new Date(dateValue);
    }
    
    // Vérifier si la date est valide
    if (isNaN(date.getTime())) {
      return 'Date invalide';
    }
    
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Déterminer si l'abonnement est actif
  const isSubscriptionActive = user.isSubscriptionActive || user.plan_actif || false;
  
  // Déterminer la date d'expiration
  const subscriptionEndDate = user.subscriptionEndDate || user.subscriptionEnd || null;
  
  // Obtenir le nom de l'abonnement
  const getSubscriptionName = (type) => {
    switch (type) {
      case 'free': return 'Gratuit';
      case 'basic': return 'Basic';
      case 'premium': return 'Premium';
      case 'unlimited': return 'Unlimited';
      case 'pro': return 'Pro';
      case 'elite': return 'Elite';
      default: return 'Aucun';
    }
  };

  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-md-4">
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h3 className="card-title">Profil</h3>
              <p><strong>Nom :</strong> {user.name}</p>
              <p><strong>Email :</strong> {user.email}</p>
              <p><strong>Membre depuis :</strong> {formatDate(user.createdAt)}</p>
              <button className="btn btn-danger mt-3" onClick={handleLogout}>
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
        
        <div className="col-md-8">
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h3 className="card-title">Abonnement</h3>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <p className="mb-1"><strong>Type :</strong> {getSubscriptionName(user.subscriptionType)}</p>
                  <p className="mb-1"><strong>Statut :</strong> {isSubscriptionActive ? 
                    <span className="badge bg-success">Actif</span> : 
                    <span className="badge bg-danger">Inactif</span>}
                  </p>
                  {subscriptionEndDate && (
                    <p className="mb-1"><strong>Expire le :</strong> {formatDate(subscriptionEndDate)}</p>
                  )}
                </div>
                <div>
                  <Link to="/subscription-plans" className="btn btn-primary me-2">
                    Changer d'abonnement
                  </Link>
                  {user.subscriptionType !== 'free' && isSubscriptionActive && (
                    <button className="btn btn-outline-danger" onClick={handleCancelSubscription}>
                      Annuler l'abonnement
                    </button>
                  )}
                </div>
              </div>
              
              <hr />
              
              <h4>Limites actuelles</h4>
              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h5 className="card-title">Tournois</h5>
                      <p className="card-text">
                        {user.tournamentsLimit ? (
                          <>
                            <strong>{user.tournamentsCreated || 0}</strong> / <strong>{user.tournamentsLimit}</strong>
                          </>
                        ) : user.subscriptionType === 'unlimited' ? (
                          <strong>Illimités</strong>
                        ) : (
                          <strong>{user.tournamentsCreated || 0} / 1</strong>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h5 className="card-title">Athlètes</h5>
                      <p className="card-text">
                        {user.athletesLimit ? (
                          <>
                            <strong>{user.athletesCount || 0}</strong> / <strong>{user.athletesLimit}</strong>
                          </>
                        ) : user.subscriptionType === 'unlimited' ? (
                          <strong>Illimités</strong>
                        ) : user.subscriptionType === 'premium' ? (
                          <strong>Maximum 200</strong>
                        ) : user.subscriptionType === 'basic' ? (
                          <strong>Maximum 50</strong>
                        ) : (
                          <strong>Maximum 10</strong>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {user.oneTimePurchases && user.oneTimePurchases.length > 0 && (
            <div className="card shadow-sm">
              <div className="card-body">
                <h3 className="card-title">Achats à l'unité</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Tournoi</th>
                      <th>Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {user.oneTimePurchases.map((purchase, index) => (
                      <tr key={index}>
                        <td>{formatDate(purchase.purchaseDate)}</td>
                        <td>{purchase.tournamentId || 'Tournoi'}</td>
                        <td>{purchase.amount}€</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
